import React, { useEffect, useState } from 'react';
import { AppBar, Badge, Box, useTheme } from '@mui/material';
import { useContext } from 'react';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { observer } from 'mobx-react-lite';
import IconLogo from 'shared/icons/logo';
import { borderRadiusValues, zIndexes } from 'shared/lib/utils/uiConstants';
import { useNavigate } from 'react-router-dom';
import IconBurger from 'shared/icons/burger';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import { useGeneralAdaptive } from 'shared/hooks/useGeneralAdaptive';
import { defaultSpacingPresets } from 'shared/hooks/useGetSpacing';
import { makeTransition } from 'shared/lib/utils/utils';

export default observer(function Header({
  onMainMenuClick,
}: {
  onMainMenuClick: () => void;
}) {
  const theme = useTheme();
  const {
    getFontSize,
    spacings: { sp, icons },
  } = useContext(UiContext);
  const {
    mobxStore: { authStore, adminRequestsStore, explorerStore },
  } = useContext(MobxContext);

  const [isScrollUp, setIsScrollUp] = useState(false);
  const [height, setHeight] = useState(0);

  const user = authStore.user;
  const isAdmin = user?.isAdmin;

  const navigate = useNavigate();

  const { generalPaddingX } = useGeneralAdaptive();

  useEffect(() => {
    let touchstartY = 0;
    let touchendY = 0;

    const wheelListener = (e: WheelEvent) => {
      setIsScrollUp(e.deltaY < 0);
    };
    const touchStartListener = (e: TouchEvent) => {
      touchstartY = e.changedTouches[0].screenY;
    };
    const touchMoveListener = (e: TouchEvent) => {
      touchendY = e.changedTouches[0].screenY;
      const deltaY = touchstartY - touchendY;
      setIsScrollUp(deltaY < 0);
    };

    document.addEventListener('wheel', wheelListener);
    document.addEventListener('touchstart', touchStartListener);
    document.addEventListener('touchmove', touchMoveListener);

    return () => {
      document.removeEventListener('wheel', wheelListener);
      document.removeEventListener('touchstart', touchStartListener);
      document.removeEventListener('touchmove', touchMoveListener);
    };
  }, []);

  return (
    <AppBar
      ref={(el) => {
        if (el) {
          setHeight(el.offsetHeight);
        }
      }}
      position="sticky"
      sx={{
        top: isScrollUp ? 0 : -height,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        px: generalPaddingX,
        py: sp.s40,
        borderBottom: 1,
        borderColor: 'custom.lightGrey',
        zIndex: zIndexes.header,
        bgcolor: theme.palette.secondary.main,
        transition: makeTransition(['top']),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: 1,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: 1440,
        }}
      >
        <IconLogo
          onClick={() => {
            explorerStore.clearCurrentEntity();
            navigate('/', { replace: false });
          }}
        ></IconLogo>

        <Box
          sx={{
            'display': 'flex',
            'alignItems': 'center',
            'gap': defaultSpacingPresets.sp.s60,
            '& .avatar': {
              width: icons['48x48'].w,
              height: icons['48x48'].h,
              borderRadius: theme.spacing(borderRadiusValues.round),
              cursor: 'pointer',
              objectFit: 'cover',
            },
          }}
        >
          {user && (
            <img
              className="avatar"
              role="button"
              src={user.avatar || user.avatarPlaceholder}
              alt="avatar"
              onClick={() =>
                navigate({
                  pathname: '/profile',
                })
              }
            ></img>
          )}
          <Box
            sx={{
              '& .MuiBadge-badge': {
                p: 0,
                width: icons['24x24'].w,
                height: icons['24x24'].h,
                borderRadius: 10,
                border: 1,
                transform: 'translate(55%, -45%)',
                borderColor: 'primary.contrastText',
                minWidth: 'unset',
                bgcolor: '#C95D5D',
                display:
                  !isAdmin || !adminRequestsStore?.requests?.length
                    ? 'none'
                    : '',
              },
            }}
          >
            <Badge
              badgeContent={adminRequestsStore?.requests?.length || null}
              invisible={!isAdmin || !adminRequestsStore?.requests?.length}
            >
              <IconBurger onClick={onMainMenuClick} />
            </Badge>
          </Box>
        </Box>
      </Box>
    </AppBar>
  );
});
